
import React, { useEffect, useState,useMemo,useRef} from "react";
import axios from "axios";
import {
  Table,
  Spinner,
  Alert,
  Card,
  Form,
  Row,
  Col,
  Button,
  Modal,
  ButtonGroup,
  Dropdown,
  Badge,
  Nav,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import "chart.js/auto";
import { useRefresh } from "./RefreshContext";
import { useAuth } from "../contexts/AuthContext";
import "../ComponentsCss/GlobalRiskOverview.css";
import { Toast, ToastContainer } from "react-bootstrap";
import { useDomainUsers } from "../contexts/DomainUsersProvider";

const GlobalRiskOverview = ({ userData }) => {
  // ============ CORE DATA STATES ============
  const {
    allDomainUsers,
    setAllDomainUsers,
    loadingAllUsers,
    insights,
    setInsights,
    recommendedModules,
    setRecommendedModules,
    score,
    setScore,
    importedUsersInvites,
    setImportedUsersInvites,
    importedUsersInternalInvites,
    setImportedUsersInternalInvites,
    avoidableLossData,
    setAvoidableLossData,
    externalPDFs,
    setExternalPDFs,
    managedRisks,
    setManagedRisks,
    importedEmails,
    setImportedEmails,
    loading,
    error,
    autoConsolidated,
    setAutoConsolidated,
    autoConsolidateLoading,
    consolidationProgress,
    loadingExternalInvites,
    loadingInternalInvites,
    loadingAvoidableLoss,
    fetchAllDomainUsers,
    fetchConsolidatedData,
    fetchAllUsersConsolidated,
    fetchUserExternalInvites,
    fetchUserInternalInvites,
    fetchUserAvoidableLoss,
    fetchUserExternalPDFs,
    fetchUserManagedRisks,
    invalidateCache,
    deduplicateInsights,
    extractDomain,
  } = useDomainUsers();

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  // ============ CONSOLIDATED VIEW STATES ============
  const [mainDashboardTab, setMainDashboardTab] = useState("insights");
// Add this near the top of GlobalRiskOverview, alongside other state/ref declarations
const isManualRefreshing = useRef(false);
  const btnPrimary = {
    padding: "7px 16px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    backgroundColor: "#024A82",
    color: "#FFFFF",
    fontFamily: "'Inter', sans-serif",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  };

  // ============ MODAL STATES ============
  const [showRiskDetailModal, setShowRiskDetailModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [modalDashboardData, setModalDashboardData] = useState({
    insights: [],
    recommendedModules: [],
    score: null,
    userEmail: "",
    externalInvites: [],
    internalInvites: [],
    avoidableLossData: [],
    externalPDFs: [],
    managedRisks: [],
    loading: true,
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [selectedInsightForDetail, setSelectedInsightForDetail] =
    useState(null);
  const [showInsightDetailModal, setShowInsightDetailModal] = useState(false);

  // ============ ADMIN & DOMAIN USERS ============

  const [isAdmin, setIsAdmin] = useState(false);

  // ============ AUTO-CONSOLIDATION ============

  // ============ FILTER STATES ============
  const [severityFilter, setSeverityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [ownerFilter, setOwnerFilter] = useState("All");

  // ============ SHARED DASHBOARD STATES ============
  const [sharedWithMeDashboards, setSharedWithMeDashboards] = useState([]);
  const [loadingSharedWithMe, setLoadingSharedWithMe] = useState(false);
  const [showSharedWithMeModal, setShowSharedWithMeModal] = useState(false);
  const [importingSharedDashboard, setImportingSharedDashboard] =
    useState(null);
  const [importedSharedDashboards, setImportedSharedDashboards] = useState(
    new Set(),
  );
  const [refreshingSharedDashboard, setRefreshingSharedDashboard] =
    useState(null);

  // ============ LOADING STATES ============

  const { refreshKey } = useRefresh();
  const { profile } = useAuth();

  const brandColors = {
    primaryGreen: "#6BCEEF",
    secondaryBlue: "#388fd2ff",
    tertiaryYellow: "#FFFF00",
  };

  const DASHBOARD_SHARE_ENDPOINTS = {
    sharedWithMe:
      'https://ag3iybvtg6.execute-api.ap-southeast-2.amazonaws.com/prod/api/dashboard/shared-with-me',
    getSharedDashboard:
      'https://aa6htq1l5m.execute-api.ap-southeast-2.amazonaws.com/prod/api/dashboard/shared',
  };

  const triggerToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };
  const getUserRegion = () => sessionStorage.getItem("userRegion") || "GB";
  const invitedByUserId = userData?.["cognito:username"];
  const region = getUserRegion();
  const currentUserId = userData?.["cognito:username"];
  const currentUserEmail = userData?.email || profile?.email;

  const safeText = (value, fallback = "NA") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "object") {
      if ("S" in value) return value.S;
      if ("N" in value) return value.N;
      try {
        return JSON.stringify(value);
      } catch {
        return fallback;
      }
    }
    return String(value);
  };

  // ============ INIT: Check admin & fetch users ============
  useEffect(() => {
    const userGroups = userData?.cognitogroups || [];
    const roleFromProfile = profile?.role;
    const adminStatus =
      userGroups.includes("Admin") || roleFromProfile === "ADMIN";
    setIsAdmin(adminStatus);

    // Now passes arguments to the context function
    fetchAllDomainUsers(currentUserEmail, profile?.orgId, region);
  }, [userData, profile]);

  // ============ AUTO-CONSOLIDATE on domain users load ============
 // ✅ FIX — guard with ref only, remove state from deps
useEffect(() => {
  if (allDomainUsers.length === 0|| isManualRefreshing.current) return;
  const idleId = requestIdleCallback(
    () => fetchAllUsersConsolidated(allDomainUsers, region, triggerToast),
    { timeout: 3000 }
  );
  return () => cancelIdleCallback(idleId);
}, [allDomainUsers]); // ← ONLY allDomainUsers; the ref in the context guards against re-runs

  // ============ FETCH DOMAIN USERS ============

  const getSeverityCounts = (list) => {
    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    list.forEach((item) => {
      if (counts[item.severity] !== undefined) counts[item.severity]++;
    });
    return counts;
  };

  // FETCH CURRENT USER'S OWN ASSESSMENT
  const fetchOwnAssessment = async () => {
    try {
      const response = await axios.get(
        "https://y56vrkh658.execute-api.ap-southeast-2.amazonaws.com/prod/api/user-assessment",
        {
          params: { email: currentUserEmail },
          headers: { Region: region },
        },
      );
      const data = response.data;
      if (data?.insights?.length > 0) {
        const ownInsights = data.insights.map((ins) => ({
          ...ins,
          sourceEmail: currentUserEmail,
          source: "Own Assessment",
          importedAt: new Date().toISOString(),
        }));
        setInsights((prev) => deduplicateInsights([...prev, ...ownInsights]));
      }
      if (data?.recommendedModules?.length > 0) {
        setRecommendedModules((prev) =>
          Array.from(new Set([...prev, ...data.recommendedModules])),
        );
      }
      if (data?.score !== undefined) {
        setScore((prev) =>
          prev === null ? data.score : Math.round((prev + data.score) / 2),
        );
      }
    } catch (err) {
      console.warn("Failed to fetch own assessment:", err.message);
    }
  };

  //  "FETCH BASE CONSOLIDATED DATA" useEffect:
  useEffect(() => {
    fetchConsolidatedData(currentUserId, region);
    fetchOwnAssessment();
  }, [currentUserId, region]);

  // ============ SHARED DASHBOARD FUNCTIONS ============
  const fetchSharedWithMe = async () => {
    if (!currentUserEmail) return;
    setLoadingSharedWithMe(true);
    try {
      const response = await axios.get(DASHBOARD_SHARE_ENDPOINTS.sharedWithMe, {
        params: { email: currentUserEmail },
        headers: { Region: region },
      });
      if (response.status === 200) {
        setSharedWithMeDashboards(response.data.sharedDashboards || []);
        triggerToast(
          `✅ Found ${response.data.sharedDashboards?.length || 0} shared dashboards`,
        );
      }
    } catch (error) {
      triggerToast("❌ Failed to load shared dashboards");
    } finally {
      setLoadingSharedWithMe(false);
    }
  };

  const handleImportSharedDashboard = async (
    shareId,
    ownerName,
    ownerEmail,
  ) => {
    if (!window.confirm(`Import complete dashboard from ${ownerName}?`)) return;
    setImportingSharedDashboard(shareId);
    try {
      const response = await axios.get(
        `${DASHBOARD_SHARE_ENDPOINTS.getSharedDashboard}/${shareId}`,
        {
          params: { email: currentUserEmail },
          headers: { Region: region },
        },
      );
      if (response.status === 200 && response.data.success) {
        const { dashboardData } = response.data;
        let shareOptions = response.data.shareOptions;
        if (typeof shareOptions === "string")
          shareOptions = JSON.parse(shareOptions);
        let importedCount = 0;
        setImportedSharedDashboards((prev) => new Set([...prev, shareId]));

        if (
          shareOptions.riskRegister &&
          dashboardData.riskRegister?.risks?.length > 0
        ) {
          const importedRisks = dashboardData.riskRegister.risks.map(
            (risk, index) => ({
              ...risk,
              riskId: risk.riskId || `shared-${shareId}-${index}`,
              sourceEmail: ownerEmail,
              importedFrom: `${ownerName} (${ownerEmail})`,
              importedAt: new Date().toISOString(),
              shareId,
              source: "External Share",
            }),
          );
          setInsights((prev) =>
            deduplicateInsights([...prev, ...importedRisks]),
          );
          importedCount += importedRisks.length;
        }
        if (
          shareOptions.recommendedModules &&
          dashboardData.recommendedModules?.modules?.length > 0
        ) {
          setRecommendedModules((prev) =>
            Array.from(
              new Set([...prev, ...dashboardData.recommendedModules.modules]),
            ),
          );
        }
        if (
          shareOptions.securityScore &&
          dashboardData.securityScore?.currentScore != null
        ) {
          const importedScore = dashboardData.securityScore.currentScore;
          setScore((prevScore) =>
            prevScore === null
              ? importedScore
              : Math.round((prevScore + importedScore) / 2),
          );
        }
        if (
          shareOptions.thirdPartyCollaboration &&
          dashboardData.thirdPartyCollaboration
        ) {
          const invites = Array.isArray(dashboardData.thirdPartyCollaboration)
            ? dashboardData.thirdPartyCollaboration
            : dashboardData.thirdPartyCollaboration.invites || [];
          if (invites.length > 0)
            setImportedUsersInvites((prev) => [
              ...prev,
              ...invites.map((inv) => ({
                ...inv,
                importedFrom: `${ownerName} (${ownerEmail})`,
                shareId,
              })),
            ]);
        }
        if (shareOptions.internalInvites && dashboardData.internalInvites) {
          const invites = Array.isArray(dashboardData.internalInvites)
            ? dashboardData.internalInvites
            : dashboardData.internalInvites.invites || [];
          if (invites.length > 0)
            setImportedUsersInternalInvites((prev) => [
              ...prev,
              ...invites.map((inv) => ({
                ...inv,
                importedFrom: `${ownerName} (${ownerEmail})`,
                shareId,
              })),
            ]);
        }
        if (shareOptions.avoidableLoss && dashboardData.avoidableLoss) {
          const lossData = Array.isArray(dashboardData.avoidableLoss)
            ? dashboardData.avoidableLoss
            : dashboardData.avoidableLoss.calculations || [];
          if (lossData.length > 0)
            setAvoidableLossData((prev) => [
              ...prev,
              ...lossData.map((c) => ({
                ...c,
                importedFrom: `${ownerName} (${ownerEmail})`,
                shareId,
              })),
            ]);
        }
        if (
          shareOptions.reporting &&
          dashboardData.reporting?.uploadedFiles?.length > 0
        ) {
          setExternalPDFs((prev) => [
            ...prev,
            ...dashboardData.reporting.uploadedFiles.map((f) => ({
              ...f,
              importedFrom: `${ownerName} (${ownerEmail})`,
              shareId,
            })),
          ]);
        }
        if (
          shareOptions.reporting &&
          dashboardData.reporting?.managedRisks?.length > 0
        ) {
          setManagedRisks((prev) => [
            ...prev,
            ...dashboardData.reporting.managedRisks.map((r) => ({
              ...r,
              importedFrom: `${ownerName} (${ownerEmail})`,
              shareId,
            })),
          ]);
        }
        triggerToast(
          `✅ Imported dashboard from ${ownerName}! ${importedCount} risks merged.`,
        );
        setShowSharedWithMeModal(false);
        fetchSharedWithMe();
      } else {
        throw new Error(response.data.message || "Access denied");
      }
    } catch (error) {
      triggerToast(
        error.response?.data?.message || "Failed to import dashboard.",
      );
    } finally {
      setImportingSharedDashboard(null);
    }
  };

  const handleRefreshSharedDashboard = async (
    shareId,
    ownerName,
    ownerEmail,
  ) => {
    setRefreshingSharedDashboard(shareId);
    try {
      // Remove old data
      setInsights((prev) => prev.filter((i) => i.shareId !== shareId));
      setImportedUsersInvites((prev) =>
        prev.filter((i) => i.shareId !== shareId),
      );
      setImportedUsersInternalInvites((prev) =>
        prev.filter((i) => i.shareId !== shareId),
      );
      setAvoidableLossData((prev) => prev.filter((i) => i.shareId !== shareId));
      setExternalPDFs((prev) => prev.filter((i) => i.shareId !== shareId));
      setManagedRisks((prev) => prev.filter((i) => i.shareId !== shareId));

      const response = await axios.get(
        `${DASHBOARD_SHARE_ENDPOINTS.getSharedDashboard}/${shareId}`,
        {
          params: { email: currentUserEmail },
          headers: { Region: region },
        },
      );
      if (response.status === 200 && response.data.success) {
        const { dashboardData } = response.data;
        let shareOptions = response.data.shareOptions;
        if (typeof shareOptions === "string")
          shareOptions = JSON.parse(shareOptions);

        if (
          shareOptions.riskRegister &&
          dashboardData.riskRegister?.risks?.length > 0
        ) {
          const importedRisks = dashboardData.riskRegister.risks.map(
            (risk, index) => ({
              ...risk,
              riskId: risk.riskId || `shared-${shareId}-${index}`,
              sourceEmail: ownerEmail,
              importedFrom: `${ownerName} (${ownerEmail})`,
              importedAt: new Date().toISOString(),
              shareId,
              source: "External Share",
            }),
          );
          setInsights((prev) =>
            deduplicateInsights([...prev, ...importedRisks]),
          );
        }
        if (
          shareOptions.recommendedModules &&
          dashboardData.recommendedModules?.modules?.length > 0
        ) {
          setRecommendedModules((prev) =>
            Array.from(
              new Set([...prev, ...dashboardData.recommendedModules.modules]),
            ),
          );
        }
        if (
          shareOptions.securityScore &&
          dashboardData.securityScore?.currentScore != null
        ) {
          setScore((prevScore) =>
            prevScore === null
              ? dashboardData.securityScore.currentScore
              : Math.round(
                  (prevScore + dashboardData.securityScore.currentScore) / 2,
                ),
          );
        }
        if (
          shareOptions.thirdPartyCollaboration &&
          dashboardData.thirdPartyCollaboration
        ) {
          const invites = Array.isArray(dashboardData.thirdPartyCollaboration)
            ? dashboardData.thirdPartyCollaboration
            : dashboardData.thirdPartyCollaboration.invites || [];
          if (invites.length > 0)
            setImportedUsersInvites((prev) => [
              ...prev,
              ...invites.map((inv) => ({
                ...inv,
                importedFrom: `${ownerName} (${ownerEmail})`,
                shareId,
              })),
            ]);
        }
        if (shareOptions.internalInvites && dashboardData.internalInvites) {
          const invites = Array.isArray(dashboardData.internalInvites)
            ? dashboardData.internalInvites
            : dashboardData.internalInvites.invites || [];
          if (invites.length > 0)
            setImportedUsersInternalInvites((prev) => [
              ...prev,
              ...invites.map((inv) => ({
                ...inv,
                importedFrom: `${ownerName} (${ownerEmail})`,
                shareId,
              })),
            ]);
        }
        if (shareOptions.avoidableLoss && dashboardData.avoidableLoss) {
          const lossData = Array.isArray(dashboardData.avoidableLoss)
            ? dashboardData.avoidableLoss
            : dashboardData.avoidableLoss.calculations || [];
          if (lossData.length > 0)
            setAvoidableLossData((prev) => [
              ...prev,
              ...lossData.map((c) => ({
                ...c,
                importedFrom: `${ownerName} (${ownerEmail})`,
                shareId,
              })),
            ]);
        }
        if (
          shareOptions.reporting &&
          dashboardData.reporting?.uploadedFiles?.length > 0
        ) {
          setExternalPDFs((prev) => [
            ...prev,
            ...dashboardData.reporting.uploadedFiles.map((f) => ({
              ...f,
              importedFrom: `${ownerName} (${ownerEmail})`,
              shareId,
            })),
          ]);
        }
        if (
          shareOptions.reporting &&
          dashboardData.reporting?.managedRisks?.length > 0
        ) {
          setManagedRisks((prev) => [
            ...prev,
            ...dashboardData.reporting.managedRisks.map((r) => ({
              ...r,
              importedFrom: `${ownerName} (${ownerEmail})`,
              shareId,
            })),
          ]);
        }
        triggerToast(`✅ Refreshed dashboard from ${ownerName}!`);
      }
    } catch (error) {
      triggerToast(
        error.response?.data?.message || "Failed to refresh dashboard.",
      );
    } finally {
      setRefreshingSharedDashboard(null);
    }
  };

  const handleRefreshAllImportedDashboards = async () => {
    if (importedSharedDashboards.size === 0) {
      triggerToast("❌ No imported dashboards to refresh");
      return;
    }
    triggerToast(
      `🔄 Refreshing ${importedSharedDashboards.size} imported dashboard(s)...`,
    );
    setLoadingSharedWithMe(true);
    try {
      const importedDashboardsList = sharedWithMeDashboards.filter((d) =>
        importedSharedDashboards.has(d.shareId),
      );
      let successCount = 0,
        failCount = 0;
      for (const dashboard of importedDashboardsList) {
        try {
          await handleRefreshSharedDashboard(
            dashboard.shareId,
            dashboard.ownerName,
            dashboard.ownerEmail,
          );
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      if (successCount > 0 && failCount === 0)
        triggerToast(`✅ Refreshed all ${successCount} dashboard(s)!`);
      else if (successCount > 0)
        triggerToast(`⚠️ Refreshed ${successCount}. ${failCount} failed.`);
      else triggerToast(`❌ Failed to refresh ${failCount} dashboard(s).`);
    } catch (error) {
      triggerToast("❌ Failed to refresh imported dashboards");
    } finally {
      setLoadingSharedWithMe(false);
    }
  };

  const handleRemoveSharedDashboard = async (
    shareId,
    ownerName,
    ownerEmail,
  ) => {
    if (
      !window.confirm(`Remove all imported data from ${ownerName}'s dashboard?`)
    )
      return;
    try {
      setInsights((prev) => prev.filter((i) => i.shareId !== shareId));
      setImportedUsersInvites((prev) =>
        prev.filter((i) => i.shareId !== shareId),
      );
      setImportedUsersInternalInvites((prev) =>
        prev.filter((i) => i.shareId !== shareId),
      );
      setAvoidableLossData((prev) => prev.filter((i) => i.shareId !== shareId));
      setExternalPDFs((prev) => prev.filter((i) => i.shareId !== shareId));
      setManagedRisks((prev) => prev.filter((i) => i.shareId !== shareId));
      setImportedSharedDashboards((prev) => {
        const s = new Set(prev);
        s.delete(shareId);
        return s;
      });
      triggerToast(`✅ Removed imported data from ${ownerName}`);
    } catch (error) {
      triggerToast("Failed to remove dashboard data");
    }
  };
// ===== HANDLE RE-CONSOLIDATE (Manual Refresh Button) =====
const handleReconsolidate = async () => {
  isManualRefreshing.current = true; // suppress the auto-consolidation useEffect
  try {
    invalidateCache();
    await fetchConsolidatedData(currentUserId, region);
    await fetchOwnAssessment();
    const users = await fetchAllDomainUsers(currentUserEmail, profile?.orgId, region);
    if (users.length > 0) {
      await fetchAllUsersConsolidated(users, region, triggerToast);
    }
  } finally {
    isManualRefreshing.current = false; // always reset, even if an await throws
  }
};
  // ============ ADMIN VIEW USER DASHBOARD ============
  const handleViewUserDashboard = async (userEmail) => {
    if (!isAdmin) {
      triggerToast("❌ Admin access required");
      return;
    }
    const currentDomain = extractDomain(currentUserEmail);
    const targetDomain = extractDomain(userEmail);
    if (currentDomain !== targetDomain) {
      triggerToast("❌ You can only view dashboards from your domain");
      return;
    }
    try {
      setModalDashboardData({
        insights: [],
        recommendedModules: [],
        score: null,
        userEmail,
        externalInvites: [],
        internalInvites: [],
        avoidableLossData: [],
        externalPDFs: [],
        managedRisks: [],
        loading: true,
      });
      setShowDashboardModal(true);
      const [
        dashboardResponse,
        externalInvites,
        internalInvites,
        avoidableLossCalcs,
        userPDFs,
        userManagedRisks,
      ] = await Promise.all([
        axios.get(
          "https://y56vrkh658.execute-api.ap-southeast-2.amazonaws.com/prod/api/user-assessment",
          { params: { email: userEmail }, headers: { Region: region } },
        ),
        fetchUserExternalInvites(userEmail, region), // <-- added region
        fetchUserInternalInvites(userEmail, region), // <-- added region
        fetchUserAvoidableLoss(userEmail, region), // <-- added region
        fetchUserExternalPDFs(userEmail, region), // <-- added region
        fetchUserManagedRisks(userEmail, region), // <-- added region
      ]);
      const data = dashboardResponse.data;
      if (data) {
        let processedInsights = [];
        if (data.insights?.length > 0)
          processedInsights = data.insights.map((i) => ({
            ...i,
            sourceEmail: userEmail,
          }));
        setModalDashboardData({
          insights: processedInsights,
          recommendedModules: data.recommendedModules || [],
          score: data.score ?? null,
          userEmail,
          externalInvites: externalInvites || [],
          internalInvites: internalInvites || [],
          avoidableLossData: avoidableLossCalcs || [],
          externalPDFs: userPDFs || [],
          managedRisks: userManagedRisks || [],
          loading: false,
        });
        triggerToast(`✅ Dashboard opened for ${userEmail}`);
      } else {
        setModalDashboardData({
          insights: [],
          recommendedModules: [],
          score: null,
          userEmail,
          externalInvites: [],
          internalInvites: [],
          avoidableLossData: [],
          externalPDFs: [],
          managedRisks: [],
          loading: false,
          error: "No dashboard data found",
        });
      }
    } catch (error) {
      setModalDashboardData({
        insights: [],
        recommendedModules: [],
        score: null,
        userEmail,
        externalInvites: [],
        avoidableLossData: [],
        externalPDFs: [],
        managedRisks: [],
        loading: false,
        error:
          error.response?.status === 404
            ? "No assessment data found"
            : "Failed to load dashboard",
      });
    }
  };

  const handleViewManagedRisk = (risk) => {
    setSelectedRisk(risk);
    setShowRiskDetailModal(true);
  };

  const handleShareDashboard = async () => {
    try {
      await axios.post(
        "https://yyq7xdxonj.execute-api.ap-southeast-2.amazonaws.com/prod/api/share-dashboard",
        {
          userId: currentUserId,
          email: recipientEmail,
          score,
          recommendedModules,
          insights,
        },
        { headers: { Region: region } },
      );
      alert("Dashboard shared successfully!");
      setShowShareModal(false);
      setRecipientEmail("");
    } catch (error) {
      alert("Failed to share dashboard.");
    }
  };

 

  // ============ HELPER: Source type ============
  const getSourceType = (insight) => {
    if (insight.source === "External Share") return "Shared";
    if (insight.source === "Own Assessment") return "Own"; // ADD THIS LINE
    if (insight.sourceEmail) return "Imported";
    return "Native";
  };

  const getSourceBadge = (insight) => {
    const type = getSourceType(insight);
    const styleMap = {
      Shared: { borderColor: "#6f42c1", color: "#6f42c1" },
      Imported: { borderColor: "#035EA5", color: "#035EA5" },
      Own: { borderColor: "#198754", color: "#198754" },
      Native: { borderColor: "#6c757d", color: "#6c757d" },
    };
    return (
      <Badge
        bg=""
        className="border"
        style={styleMap[type] || { borderColor: "#6c757d", color: "#6c757d" }}
      >
        {type}
      </Badge>
    );
  };

  // ============ FILTERED DATA ============
  // const filteredInsights = insights.filter((insight) => {
  //   return (
  //     (severityFilter === "All" || insight.severity === severityFilter) &&
  //     (categoryFilter === "All" || insight.category === categoryFilter) &&
  //     (ownerFilter === "All" || (insight.sourceEmail || "Own") === ownerFilter)
  //   );
  // });

  // const severityCounts = getSeverityCounts(filteredInsights);

  // const pieData = {
  //   labels: ["Critical", "High", "Medium", "Low"],
  //   datasets: [
  //     {
  //       data: [
  //         severityCounts["Critical"],
  //         severityCounts["High"],
  //         severityCounts["Medium"],
  //         severityCounts["Low"],
  //       ],
  //       backgroundColor: ["#dc3545", "#fd7e14", "#ffc107", "#198754"],
  //     },
  //   ],
  // };

  // const uniqueCategories = [...new Set(insights.map((i) => i.category))];
  // const uniqueOwners = [
  //   ...new Set(insights.map((i) => i.sourceEmail || "Own").filter(Boolean)),
  // ];

  // // ============ COMPUTED KPIs ============
  // const totalAnnualLoss = avoidableLossData.reduce((sum, calc) => {
  //   const val = parseFloat(calc.annualLoss || calc.estimatedLoss || 0);
  //   return sum + (isNaN(val) ? 0 : val);
  // }, 0);

  // const criticalCount = severityCounts["Critical"];
  // const highCount = severityCounts["High"];
// ✅ AFTER — each value only recomputes when its specific dependency changes

const filteredInsights = useMemo(() =>
  insights.filter(insight =>
    (severityFilter === 'All' || insight.severity === severityFilter) &&
    (categoryFilter === 'All' || insight.category === categoryFilter) &&
    (ownerFilter === 'All' || insight.sourceEmail === 'Own' || insight.sourceEmail === ownerFilter)
  ),
[insights, severityFilter, categoryFilter, ownerFilter]);

const severityCounts = useMemo(
  () => getSeverityCounts(filteredInsights),
  [filteredInsights]
);

const pieData = useMemo(() => ({
  labels: ['Critical', 'High', 'Medium', 'Low'],
  datasets: [{ data: [severityCounts.Critical, severityCounts.High, severityCounts.Medium, severityCounts.Low], backgroundColor: ['#dc3545', '#fd7e14', '#ffc107', '#198754'] }],
}), [severityCounts]);

const uniqueCategories = useMemo(
  () => [...new Set(insights.map(i => i.category))],
  [insights]
);

const uniqueOwners = useMemo(
  () => [...new Set(insights.map(i => i.sourceEmail).filter(Boolean))].filter(e => e !== 'Own'),
  [insights]
);

// COMPUTED KPIs
const totalAnnualLoss = useMemo(() =>
  avoidableLossData.reduce((sum, calc) => {
    const val = parseFloat(calc.annualLoss ?? calc.estimatedLoss ?? 0);
    return sum + (isNaN(val) ? 0 : val);
  }, 0),
[avoidableLossData]);

const criticalCount = severityCounts.Critical;
const highCount = severityCounts.High;
  // ============ RENDER ============
  return (
    // Root wrapper — ensure it's the positioned parent
    <div
      className="ImportRiskTopMargin p-4"
      style={{ position: "relative", minHeight: "80vh", overflow: "hidden" }}
    >
      {/* LOADING OVERLAY */}
      {(autoConsolidateLoading ||
        (loadingSharedWithMe && importedSharedDashboards.size > 0)) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
          }}
        >
          <Card
            style={{ maxWidth: "450px", textAlign: "center" }}
            className="shadow-lg"
          >
            <Card.Body className="p-4">
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: "50px", height: "50px" }}
              />
              <h5 className="mt-3 mb-2">
                {autoConsolidateLoading
                  ? "Consolidating Organisation Data"
                  : "Refreshing External Dashboards"}
              </h5>
              {autoConsolidateLoading && consolidationProgress.total > 0 && (
                <>
                  <ProgressBar
                    animated
                    now={
                      (consolidationProgress.current /
                        consolidationProgress.total) *
                      100
                    }
                    label={`${consolidationProgress.current}/${consolidationProgress.total}`}
                    className="mb-2"
                  />
                  <small className="text-muted">
                    Fetching data from all domain users...
                  </small>
                </>
              )}
              {!autoConsolidateLoading && (
                <p className="text-muted mb-0">
                  Fetching latest live data from {importedSharedDashboards.size}{" "}
                  dashboard(s)...
                </p>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      
            {/* ============ PAGE HEADER ============ */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
              <div>
                <h4 className="mb-1" style={{ fontWeight: 600 }}> 
                  
                  Risk Insights 
                </h4>
                <div className="d-flex">
                <p className="text-muted mb-0 small">
                  unified team view {allDomainUsers.length + 1} users in your organisation
                  {/* {autoConsolidated && <Badge bg="" className="ms-2" style={{border:"1px solid #035EA5" , color:"#035EA5" }}>Synced</Badge>} */}
       
                </p>
                <Dropdown>
                    <Dropdown.Toggle  id="admin-user-dropdown" className="align-items-center ms-2 " style={{...btnPrimary , marginTop:"-5px" }}>
                      View User ({allDomainUsers.length})
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                      <Dropdown.Header className="bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">Domain Users</span>
                          <Badge bg="primary" className="text-white">@{extractDomain(currentUserEmail)}</Badge>
                        </div>
                      </Dropdown.Header>
                      {allDomainUsers.map((user) => (
                        <Dropdown.Item key={user.email} onClick={() => handleViewUserDashboard(user.email)} className="py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              
                              <span className="text-truncate" style={{ maxWidth: '180px' }} title={user.email}>{user.email}</span>
                            </div>
                            <Badge bg={user.role === 'ADMIN' ? 'primary' : 'secondary'} className="text-white ms-2">{user.role || 'USER'}</Badge>
                          </div>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  </div>
                
              </div>
              <div className="d-flex align-items-center gap-2">
                {/* Admin: View Individual User */}
                {/* {isAdmin && allDomainUsers.length > 0 && ( */}
                  {/* <Dropdown>
                    <Dropdown.Toggle  id="admin-user-dropdown" className="d-flex align-items-center" style={btnPrimary}>
                      View User ({allDomainUsers.length})
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
                      <Dropdown.Header className="bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">Domain Users</span>
                          <Badge bg="primary" className="text-white">@{extractDomain(currentUserEmail)}</Badge>
                        </div>
                      </Dropdown.Header>
                      {allDomainUsers.map((user) => (
                        <Dropdown.Item key={user.email} onClick={() => handleViewUserDashboard(user.email)} className="py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              
                              <span className="text-truncate" style={{ maxWidth: '180px' }} title={user.email}>{user.email}</span>
                            </div>
                            <Badge bg={user.role === 'ADMIN' ? 'primary' : 'secondary'} className="text-white ms-2">{user.role || 'USER'}</Badge>
                          </div>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown> */}
                {/* )} */}
      
                {/* Shared With Me */}
                <Button variant="" onClick={() => { setShowSharedWithMeModal(true); fetchSharedWithMe(); }} className="d-flex align-items-center text-white" style={{height:"33px" ,...btnPrimary}}>
                  External Shares
                  {sharedWithMeDashboards.length > 0 && <Badge bg="danger" className="ms-2 rounded-pill">{sharedWithMeDashboards.length}</Badge>}
                </Button>
      
                {/* Share Dashboard */}
                <Button variant="" onClick={() => setShowShareModal(true)} className="d-flex align-items-center text-white" style={{height:"33px",...btnPrimary}}>
                  Share
                </Button>  
        
                {/* Refresh */}
                <Button  onClick={handleReconsolidate} title="Re-consolidate all data" style={{  height: '33px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' ,...btnPrimary }}>
                    Refresh
                      </Button>
              </div>
            </div>
      
      {loading && <Spinner animation="border" variant="primary" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* ============ KPI CARDS ============ */}
      {!loading && !error && (
        <>
          <Row className="mb-4 g-3">
            <Col xs={6} md={4} lg>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderLeft: "4px solid #dc3545" }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted mb-1 small">Security Score</p>
                      <h3
                        className="mb-0 fw-bold"
                        style={{
                          color:
                            score !== null && score < 40
                              ? "#dc3545"
                              : score < 70
                                ? "#fd7e14"
                                : "#198754",
                        }}
                      >
                        {score !== null ? `${score}%` : "N/A"}
                      </h3>
                    </div>
                    {/* <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(220,53,69,0.1)' }}>
                      <i className="fas fa-chart-line" style={{ fontSize: '20px', color: '#dc3545' }}></i>
                    </div> */}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={4} lg>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderLeft: "4px solid #0d6efd" }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted mb-1 small">Total Risks</p>
                      <h3 className="mb-0 fw-bold text-dark">
                        {insights.length}
                      </h3>
                    </div>
                    {/* <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(13,110,253,0.1)' }}>
                      <i className="fas fa-shield-alt" style={{ fontSize: '20px', color: '#0d6efd' }}></i>
                    </div> */}
                  </div>
                  <div className="ImportRiskTopMargin">
                    <small className="text-muted">
                      <span className="text-danger fw-bold">
                        {criticalCount}
                      </span>{" "}
                      Critical ·{" "}
                      <span style={{ color: "#fd7e14" }} className="fw-bold">
                        {highCount}
                      </span>{" "}
                      High
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={4} lg>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderLeft: "4px solid #198754" }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted mb-1 small">Managed Risks</p>
                      <h3 className="mb-0 fw-bold text-dark">
                        {managedRisks.length}
                      </h3>
                    </div>
                    {/* <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(25,135,84,0.1)' }}>
                      <i className="fas fa-check-circle" style={{ fontSize: '20px', color: '#198754' }}></i>
                    </div> */}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={4} lg>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderLeft: "4px solid #6f42c1" }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted mb-1 small ">Avoidable Loss</p>
                      <h3 className="mb-0 fw-bold text-dark" >
                        $
                        {totalAnnualLoss >= 1000
                          ? `${(totalAnnualLoss / 1000).toFixed(1)}K` 
                          : totalAnnualLoss.toFixed(0)}
                      </h3>
                    </div>
                    {/* <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(111,66,193,0.1)' }}>
                      <i className="fas fa-dollar-sign" style={{ fontSize: '20px', color: '#6f42c1' }}></i>
                    </div> */}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={4} lg>
              <Card
                className="shadow-sm border-0 h-100"
                style={{ borderLeft: "4px solid #0dcaf0" }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted mb-1 small">Contributors</p>
                      <h3 className="mb-0 fw-bold text-dark">
                        {allDomainUsers.filter((u) => u.isImported).length + 1}
                      </h3>
                    </div>
                    {/* <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(13,202,240,0.1)' }}>
                      <i className="fas fa-users" style={{ fontSize: '20px', color: '#0dcaf0' }}></i>
                    </div> */}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ============ TAB NAVIGATION ============ */}
          <Nav
            variant="tabs"
            activeKey={mainDashboardTab}
            onSelect={(k) => setMainDashboardTab(k)}
            className="mb-3"
          >
            <Nav.Item>
              <Nav.Link
                eventKey="insights"
                className="d-flex align-items-center"
              >
                Risk Insights
                <Badge
                  bg=""
                  className="ms-2"
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {insights.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="breakdown"
                className="d-flex align-items-center"
              >
                Severity Breakdown
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="managed-risks"
                className="d-flex align-items-center"
              >
                Managed Risks
                <Badge
                  bg=""
                  className="ms-2 "
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {managedRisks.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="invites"
                className="d-flex align-items-center"
              >
                Third-Party Invites
                <Badge
                  bg=""
                  className="ms-2"
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {importedUsersInvites.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="internal-invites"
                className="d-flex align-items-center"
              >
                Internal Invites
                <Badge
                  bg=""
                  className="ms-2 "
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {importedUsersInternalInvites.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="avoidable-loss"
                className="d-flex align-items-center"
              >
                Avoidable Loss
                <Badge
                  bg=""
                  className="ms-2 "
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {avoidableLossData.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="external-pdfs"
                className="d-flex align-items-center"
              >
                Documents
                <Badge
                  bg=""
                  className="ms-2"
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {externalPDFs?.length || 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="summary"
                className="d-flex align-items-center"
              >
                Modules
                <Badge
                  bg=""
                  className="ms-2"
                  style={{ border: "1px solid #035EA5", color: "#035EA5" }}
                >
                  {recommendedModules.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* ============ TAB: RISK INSIGHTS ============ */}
          {mainDashboardTab === "insights" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <Row className="mb-3 g-2">
                  <Col md={3}>
                    <Form.Select
                      size="sm"
                      value={severityFilter}
                      onChange={(e) => setSeverityFilter(e.target.value)}
                    >
                      <option value="All">All Severities</option>
                      <option>Critical</option>
                      <option>High</option>
                      <option>Medium</option>
                      <option>Low</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      size="sm"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="All">All Categories</option>
                      {uniqueCategories.map((cat, idx) => (
                        <option key={idx}>{cat}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      size="sm"
                      value={ownerFilter}
                      onChange={(e) => setOwnerFilter(e.target.value)}
                    >
                      <option value="All">All Sources</option>
                      {uniqueOwners.map((o, idx) => (
                        <option key={idx} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3} className="text-end">
                    <small className="text-muted">
                      Showing {filteredInsights.length} of {insights.length}{" "}
                      risks
                    </small>
                  </Col>
                </Row>

                {filteredInsights.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0 align-middle"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <thead
                        style={{ backgroundColor: "#1b2a4a", color: "#fff" }}
                      >
                        <tr>
                          <th style={{ width: "5%" }}>ID</th>
                          <th style={{ width: "10%" }}>Source</th>
                          <th style={{ width: "10%" }}>Category</th>
                          <th style={{ width: "7%" }}>Severity</th>
                          <th style={{ width: "25%" }}>Risk Description</th>
                          <th style={{ width: "12%" }}>Asset Affected</th>
                          <th style={{ width: "10%" }}>Threat Source</th>
                          <th style={{ width: "10%" }}>Owner</th>
                          <th style={{ width: "6%" }}>Date</th>
                          <th style={{ width: "5%" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInsights.map((insight, index) => (
                          <tr
                            key={index}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedInsightForDetail(insight);
                              setShowInsightDetailModal(true);
                            }}
                          >
                            <td>
                              <Badge bg="" className="text-dark">
                                {" "}
                                {insight.riskId || index + 1}
                              </Badge>
                            </td>
                            <td>{getSourceBadge(insight)}</td>
                            <td>
                              <Badge bg="" className="text-dark">
                                {insight.category}
                              </Badge>
                            </td>
                            <td>
                             <Badge
                               bg="transparent"
                               style={{
                                 border: `1px solid ${
                                   insight.severity === "Critical"
                                     ? "#DC3545"
                                     : insight.severity === "High"
                                     ? "#FD7E14"
                                     : insight.severity === "Medium" 
                                     ? "#FFC107"
                                     : "#52c41a"
                                 }`,
                                 color:
                                   insight.severity === "Critical"
                                     ? "#DC3545"
                                     : insight.severity === "High"
                                     ? "#FD7E14"
                                     : insight.severity === "Medium"
                                     ? "#FFC107"
                                     : "#52c41a",
                               }}
                             >
                               {insight.severity}
                             </Badge>
                           </td>
                            <td>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: "280px" }}
                                title={insight.risk}
                              >
                                {insight.risk}
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">
                                {insight.assetAffected || "N/A"}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {insight.threatSource || "N/A"}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {insight.riskOwner ||
                                  insight.sourceEmail ||
                                  "N/A"}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {insight.reviewDate || "N/A"}
                              </small>
                            </td>
                            <td className="">
                              <Button
                                size="sm"
                                style={btnPrimary}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInsightForDetail(insight);
                                  setShowInsightDetailModal(true);
                                }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0 text-center">
                    <i className="fas fa-info-circle me-2"></i>No risk insights
                    found matching your filters.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ============ TAB: SEVERITY BREAKDOWN ============ */}
          {mainDashboardTab === "breakdown" && (
            <Row>
              <Col md={5}>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <h6 className="mb-3">Severity Distribution</h6>
                    <div style={{ maxWidth: "300px", margin: "0 auto" }}>
                      <Pie
                        data={pieData}
                        options={{
                          responsive: true,
                          plugins: { legend: { position: "bottom" } },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={7}>
                <Card className="shadow-sm border-0">
                  <div
                    style={{
                      padding: "12px 16px",
                      maxHeight: "360px",
                      overflowY: "auto",
                    }}
                  >
                    <Card.Body>
                      <h6 className="mb-3">Breakdown by Severity</h6>
                      {["Critical", "High", "Medium", "Low"].map((sev) => {
                        const count = severityCounts[sev];
                        const total = insights.length || 1;
                        const pct = ((count / total) * 100).toFixed(1);
                        const colorMap = {
                          Critical: "#dc3545",
                          High: "#fd7e14",
                          Medium: "#ffc107",
                          Low: "#198754",
                        };
                        return (
                          <div key={sev} className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span
                                className="fw-bold"
                                style={{ color: colorMap[sev] }}
                              >
                                {sev}
                              </span>
                              <span className="text-muted">
                                {count} ({pct}%)
                              </span>
                            </div>
                            <ProgressBar
                              now={parseFloat(pct)}
                              style={{ height: "8px" }}
                              variant={
                                sev === "Critical"
                                  ? "danger"
                                  : sev === "High"
                                    ? "warning"
                                    : sev === "Medium"
                                      ? "primary"
                                      : "success"
                              }
                            />
                          </div>
                        );
                      })}

                      <hr />
                      <h6 className="mb-3">By Category</h6>
                      {uniqueCategories.map((cat, idx) => {
                        const catCount = insights.filter(
                          (i) => i.category === cat,
                        ).length;
                        return (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center mb-2"
                          >
                            <span>{cat}</span>
                            <Badge
                              bg=""
                              className=" border border-secondary text-secondary"
                            >
                              {catCount}
                            </Badge>
                          </div>
                        );
                      })}
                    </Card.Body>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

           {/* ============ TAB: MANAGED RISKS ============ */}
          {mainDashboardTab === "managed-risks" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Managed Risks — Consolidated</h6>
                  <Badge bg="" className=" border border-success text-success ">
                    {managedRisks.length} total
                  </Badge>
                </div>
                {managedRisks.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0 align-middle"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <thead
                        style={{ backgroundColor: "#1b2a4a", color: "#fff" }}
                      >
                        <tr>
                          <th style={{ minWidth: '200px' }}>Source</th>
                          <th>Risk ID</th>
                          <th>Risk</th>
                          <th>Category</th>
                          <th>Severity</th>
                          <th>Asset</th>
                          <th>Owner</th>
                          <th>Status</th>
                          <th>Action</th>
                          <th>Submitted</th>
                          <th>View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {managedRisks.map((risk, idx) => (
                          <tr key={idx}>
                            <td>
                              <Badge
                                bg=""
                                className="text-dark"
                                style={{ maxWidth: "200px" }}
                              >
                                {risk.importedFrom}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="" className="text-dark">
                                {risk.riskId}
                              </Badge>
                            </td>
                            <td>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: "150px" }}
                                title={risk.risk}
                              >
                                {risk.risk}
                              </div>
                            </td>
                            <td>
                              <Badge bg="" className="text-dark">
                                {risk.category}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg="transparent"
                                className={
                                  risk.severity === "Critical"
                                    ? "border border-danger text-danger"
                                    : risk.severity === "High"
                                      ? "border border-warning text-warning"
                                      : risk.severity === "Medium"
                                        ? "border border-primary text-primary"
                                        : "border border-success text-success"
                                }
                              >
                                {risk.severity}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {risk.assetAffected || "N/A"}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {risk.riskOwner || "N/A"}
                              </small>
                            </td>
                            <td>
                              <Badge
                                bg="transparent"
                                className={
                                  risk.status === "active"
                                    ? "border border-success text-success"
                                    : "border border-secondary text-secondary"
                                }
                              >
                                {risk.status}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="" className="text-dark">
                                {risk.actionType || "None"}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {risk.submittedAt
                                  ? new Date(
                                      risk.submittedAt,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "NA"}
                              </small>
                            </td>
                            <td>
                              <Button
                                size="sm"
                                style={btnPrimary}
                                onClick={() => handleViewManagedRisk(risk)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0 text-center">
                    <i className="fas fa-info-circle me-2"></i>No managed risks
                    found across the organisation.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ============ TAB: THIRD-PARTY INVITES ============ */}
          {mainDashboardTab === "invites" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h6 className="mb-3">Third-Party Invites — Consolidated</h6>
                {importedUsersInvites.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0 align-middle"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <thead
                        style={{ backgroundColor: "#1b2a4a", color: "#fff" }}
                      >
                        <tr>
                          <th>Source User</th>
                          <th>External User</th>
                          <th>Organisation</th>
                          <th>Status</th>
                          <th>Modules</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedUsersInvites.map((invite, idx) => (
                          <tr key={idx}>
                            <td>
                              <Badge bg="" className="text-dark">
                                {invite.importedFrom}
                              </Badge>
                            </td>
                            <td>
                              {invite.email || invite.inviteeEmail || "N/A"}
                            </td>
                            <td>
                              {invite.organization ||
                                invite.companyName ||
                                "N/A"}
                            </td>
                            <td>
                              <Badge
                                bg="transparent"
                                className={
                                  invite.status === "Accepted"
                                    ? "border border-success text-success"
                                    : "border border-warning text-warning"
                                }
                              >
                                {invite.status || "Pending"}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {invite.modules || "N/A"}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {invite.invitedAt
                                  ? new Date(
                                      invite.invitedAt,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "NA"}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0 text-center">
                    <i className="fas fa-info-circle me-2"></i>No third-party
                    invites found.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ============ TAB: INTERNAL INVITES ============ */}
          {mainDashboardTab === "internal-invites" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h6 className="mb-3">Internal Invites — Consolidated</h6>
                {importedUsersInternalInvites.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0 align-middle"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <thead
                        style={{ backgroundColor: "#1b2a4a", color: "#fff" }}
                      >
                        <tr>
                          <th>Source User</th>
                          <th>Department Email</th>
                          <th>Department Name</th>
                          <th>Contact Person</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedUsersInternalInvites.map((invite, idx) => (
                          <tr key={idx}>
                            <td>
                              <Badge bg="" className="text-dark">
                                {invite.importedFrom}
                              </Badge>
                            </td>
                            <td>{invite.email || "N/A"}</td>
                            <td>
                              <strong>{invite.departmentName}</strong>
                            </td>
                            <td>{invite.contactPerson || "N/A"}</td>
                            <td>
                              <Badge
                                bg="transparent"
                                className={
                                  invite.status === "Completed"
                                    ? "border border-success text-success"
                                    : "border border-warning text-warning"
                                }
                              >
                                {invite.status || "Pending"}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {invite.invitedAt
                                  ? new Date(
                                      invite.invitedAt,
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "NA"}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0 text-center">
                    <i className="fas fa-info-circle me-2"></i>No internal
                    invites found.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ============ TAB: AVOIDABLE LOSS ============ */}
          {mainDashboardTab === "avoidable-loss" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    Avoidable Loss Calculations — Consolidated
                  </h6>
                  <Badge bg="" className="border border-info text-info">
                    Total: $
                    {totalAnnualLoss >= 1000
                      ? `${(totalAnnualLoss / 1000).toFixed(1)}K`
                      : totalAnnualLoss.toFixed(0)}
                  </Badge>
                </div>
                {avoidableLossData.length > 0 ? (
                 <div className="table-responsive" style={{
                                       padding: "12px 16px",
                                       maxHeight: "360px",
                                       overflowY: "auto",
                                     }}>
                                     <Table
                                       hover
                                       className="mb-0 align-middle" 
                                       style={{ fontSize: "0.85rem" }}
                                     >
                                       <thead
                                         style={{ backgroundColor: "#1b2a4a", color: "#fff", position: "sticky", top: -15, zIndex: 2}}
                                       >
                                         <tr>
                                           <th>Source</th>
                                           <th>Risk</th>
                                           <th>Category</th>
                                           <th>Annual Loss</th>
                                           <th>Mitigation</th>
                                           <th>Date</th>
                                         </tr>
                                       </thead>
                                       <tbody>
                                         {avoidableLossData.map((calc, idx) => (
                                           <tr key={idx}>
                                             <td>
                                               <Badge
                                                 bg=""
                                                 className="text-dark"
                                                 style={{ textTransform: "lowercase" }}
                                               >
                                                 {calc.importedFrom}
                                               </Badge>
                                             </td>
                                             <td>
                                               <div
                                                 className="text-truncate"
                                                 style={{ maxWidth: "200px" }}
                                               >
                                                 {calc.risk || calc.riskDescription || "N/A"}
                                               </div>
                                             </td>
                                             <td>
                                               <Badge bg="" className="text-dark">
                                                 {calc.category || "N/A"}
                                               </Badge>
                                             </td>
                                             <td className="fw-bold text-danger">
                                               $
                                               {parseFloat(
                                                 calc.annualLoss || calc.estimatedLoss || 0,
                                               ).toLocaleString()}
                                             </td>
                                             <td>
                                               {calc.mitigation ||
                                                 calc.mitigationStrategy ||
                                                 "N/A"}
                                             </td>
                                             <td>
                                               <small className="text-muted">
                                                 {calc.calculatedAt
                                                   ? new Date(
                                                       calc.calculatedAt,
                                                     ).toLocaleDateString("en-US", {
                                                       year: "numeric",
                                                       month: "short",
                                                       day: "numeric",
                                                     })
                                                   : "NA"}
                                               </small>
                                             </td>
                                           </tr>
                                         ))}
                                       </tbody>
                                     </Table>
                                   </div>
                ) : (
                  <Alert variant="info" className="mb-0 text-center">
                    <i className="fas fa-info-circle me-2"></i>No avoidable loss
                    calculations found.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ============ TAB: EXTERNAL PDFs ============ */}
          {mainDashboardTab === "external-pdfs" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h6 className="mb-3">Documents — Consolidated</h6>
                {externalPDFs && externalPDFs.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0 align-middle"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <thead
                        style={{ backgroundColor: "#1b2a4a", color: "#fff" }}
                      >
                        <tr>
                          <th>Source</th>
                          <th>File Name</th>
                          <th>Type</th>
                          <th>Upload Date</th>
                          <th>Uploaded By</th>
                          <th>Region</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {externalPDFs.map((pdf, idx) => (
                          <tr key={idx}>
                            <td>
                              <Badge bg="" className="text-dark">
                                {pdf.importedFrom || "N/A"}
                              </Badge>
                            </td>
                            <td>{safeText(pdf.fileName, "Unknown")}</td>
                            <td>
                              <Badge bg="" className="text-dark">
                                {safeText(pdf.documentType, "NA")}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {safeText(pdf.uploadedAt, "NA")}
                              </small>
                            </td>
                            <td>
                              <small>{safeText(pdf.userName, "Unknown")}</small>
                            </td>
                           <td>
                                                         <Badge
                                                           bg="transparent"
                                                           className={
                                                             pdf.region === "GB"
                                                               ? "border border-dark text-dark"
                                                               : pdf.region === "US"
                                                                 ? "border border-dark text-dark"
                                                                 : pdf.region === "IN"
                                                                   ? "border border-dark text-dark"
                                                                   : "border border-dark text-dark"
                                                           }
                                                         >
                                                           {safeText(pdf.region, "GB")}
                                                         </Badge>
                                                       </td>
                            <td>
                              {pdf.presignedUrl && ( 
                                <a
                                  href={pdf.presignedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-success"
                                >
                                  Download
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0 text-center">
                    <i className="fas fa-info-circle me-2"></i>No documents
                    found.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ============ TAB: RECOMMENDED MODULES ============ */}
          {mainDashboardTab === "summary" && (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h6 className="mb-3">Recommended Modules</h6>
                {recommendedModules.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {recommendedModules.map((mod, idx) => (
                      <Badge
                        key={idx}
                        bg=""
                        className="px-3 py-2 border border-secondary text-secondary"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {mod}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>No recommended
                    modules available yet.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* ============ MODAL: VIEW USER DASHBOARD ============ */}
      <Modal
        show={showDashboardModal}
        onHide={() => setShowDashboardModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-chart-line me-2"></i>Dashboard —{" "}
            {modalDashboardData.userEmail}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {modalDashboardData.loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="ImportRiskTopMargin">Loading dashboard...</p>
            </div>
          ) : modalDashboardData.error ? (
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {modalDashboardData.error}
            </Alert>
          ) : (
            <>
              <Row className="mb-4 g-2">
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <h3 className="mb-1 text-primary">
                      {modalDashboardData.score !== null
                        ? modalDashboardData.score
                        : "NA"}
                    </h3>
                    <p className="mb-0 text-muted small">Risk Score</p>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <h3 className="mb-1 text-danger">
                      {modalDashboardData.insights.length}
                    </h3>
                    <p className="mb-0 text-muted small">Total Insights</p>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <h3 className="mb-1 text-info">
                      {modalDashboardData.externalInvites?.length || 0}
                    </h3>
                    <p className="mb-0 text-muted small">External Invites</p>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center p-3 bg-light rounded">
                    <h3 className="mb-1 text-warning">
                      {modalDashboardData.internalInvites?.length || 0}
                    </h3>
                    <p className="mb-0 text-muted small">Internal Invites</p>
                  </div>
                </Col>
              </Row>

              <Nav
                variant="tabs"
                activeKey={modalDashboardData.activeTab || "modules"}
                onSelect={(k) =>
                  setModalDashboardData((prev) => ({ ...prev, activeTab: k }))
                }
                className="mb-3"
              >
                <Nav.Item>
                  <Nav.Link eventKey="modules">
                    Modules{" "}
                    <Badge bg="secondary" className="ms-1">
                      {modalDashboardData.recommendedModules.length}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="insights">
                    Insights{" "}
                    <Badge bg="danger" className="ms-1">
                      {modalDashboardData.insights.length}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="invites">External Invites</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="internal-invites">
                    Internal Invites
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="external-pdfs">PDFs</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="managed-risks">
                    Managed Risks{" "}
                    <Badge bg="success" className="ms-1">
                      {modalDashboardData.managedRisks?.length || 0}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Modal Tab: Modules */}
              {(!modalDashboardData.activeTab ||
                modalDashboardData.activeTab === "modules") && (
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    {modalDashboardData.recommendedModules.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {modalDashboardData.recommendedModules.map(
                          (mod, idx) => (
                            <Badge
                              key={idx}
                              bg="secondary"
                              className="px-3 py-2"
                              style={{ fontSize: "0.9rem" }}
                            >
                              <i className="fas fa-check-circle me-2"></i>
                              {mod}
                            </Badge>
                          ),
                        )}
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        No recommended modules.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Modal Tab: Insights */}
              {modalDashboardData.activeTab === "insights" && (
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    {modalDashboardData.insights.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped bordered hover className="mb-0">
                          <thead className="table-dark">
                            <tr>
                              <th>Category</th>
                              <th>Severity</th>
                              <th>Risk</th>
                              <th>Asset</th>
                              <th>Recommendation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalDashboardData.insights.map(
                              (insight, index) => (
                                <tr key={index}>
                                  <td>{insight.category}</td>
                                  <td>
                                    <Badge
                                      bg={
                                        insight.severity === "Critical"
                                          ? "danger"
                                          : insight.severity === "High"
                                            ? "warning"
                                            : insight.severity === "Medium"
                                              ? "primary"
                                              : "success"
                                      }
                                    >
                                      {insight.severity}
                                    </Badge>
                                  </td>
                                  <td>{insight.risk}</td>
                                  <td>{insight.assetAffected}</td>
                                  <td>{insight.recommendation}</td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        No risk insights.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Modal Tab: External Invites */}
              {modalDashboardData.activeTab === "invites" && (
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    {modalDashboardData.externalInvites &&
                    modalDashboardData.externalInvites.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped bordered hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>External User</th>
                              <th>Organisation</th>
                              <th>Status</th>
                              <th>Modules</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalDashboardData.externalInvites.map(
                              (invite, idx) => (
                                <tr key={idx}>
                                  <td>
                                    {invite.email ||
                                      invite.inviteeEmail ||
                                      "N/A"}
                                  </td>
                                  <td>
                                    {invite.organization ||
                                      invite.companyName ||
                                      "N/A"}
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        invite.status === "Completed"
                                          ? "success"
                                          : "warning"
                                      }
                                    >
                                      {invite.status || "Pending"}
                                    </Badge>
                                  </td>
                                  <td>{invite.modules || "N/A"}</td>
                                  <td>
                                    <small>
                                      {invite.invitedAt
                                        ? new Date(
                                            invite.invitedAt,
                                          ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          })
                                        : "NA"}
                                    </small>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        No external invites.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Modal Tab: Internal Invites */}
              {modalDashboardData.activeTab === "internal-invites" && (
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    {modalDashboardData.internalInvites &&
                    modalDashboardData.internalInvites.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped bordered hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Department Email</th>
                              <th>Department Name</th>
                              <th>Contact</th>
                              <th>Status</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalDashboardData.internalInvites.map(
                              (invite, idx) => (
                                <tr key={idx}>
                                  <td>{invite.email}</td>
                                  <td>
                                    <strong>{invite.departmentName}</strong>
                                  </td>
                                  <td>{invite.contactPerson || "N/A"}</td>
                                  <td>
                                    <Badge
                                      bg={
                                        invite.status === "Completed"
                                          ? "success"
                                          : "warning"
                                      }
                                    >
                                      {invite.status || "Pending"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <small>
                                      {invite.invitedAt
                                        ? new Date(
                                            invite.invitedAt,
                                          ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          })
                                        : "NA"}
                                    </small>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        No internal invites.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Modal Tab: External PDFs */}
              {modalDashboardData.activeTab === "external-pdfs" && (
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    {modalDashboardData.externalPDFs &&
                    modalDashboardData.externalPDFs.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped bordered hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>File Name</th>
                              <th>Type</th>
                              <th>Upload Date</th>
                              <th>Uploaded By</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalDashboardData.externalPDFs.map((pdf, idx) => (
                              <tr key={idx}>
                                <td>
                                  <i className="fas fa-file-pdf text-danger me-2"></i>
                                  {safeText(pdf.fileName)}
                                </td>
                                <td>
                                  <Badge bg="secondary">
                                    {safeText(pdf.documentType)}
                                  </Badge>
                                </td>
                                <td>
                                  <small>{safeText(pdf.uploadedAt)}</small>
                                </td>
                                <td>
                                  <small>{safeText(pdf.userName)}</small>
                                </td>
                                <td>
                                  {pdf.presignedUrl && (
                                    <a
                                      href={pdf.presignedUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      <i className="fas fa-download"></i>
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        No PDFs found.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Modal Tab: Managed Risks */}
              {modalDashboardData.activeTab === "managed-risks" && (
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    {modalDashboardData.managedRisks &&
                    modalDashboardData.managedRisks.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped bordered hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Risk ID</th>
                              <th>Risk</th>
                              <th>Category</th>
                              <th>Severity</th>
                              <th>Status</th>
                              <th>View</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalDashboardData.managedRisks.map(
                              (risk, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <Badge bg="secondary">{risk.riskId}</Badge>
                                  </td>
                                  <td
                                    className="text-truncate"
                                    style={{ maxWidth: "200px" }}
                                  >
                                    {risk.risk}
                                  </td>
                                  <td>
                                    <Badge bg="info">{risk.category}</Badge>
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        risk.severity === "Critical"
                                          ? "danger"
                                          : risk.severity === "High"
                                            ? "warning"
                                            : "success"
                                      }
                                    >
                                      {risk.severity}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        risk.status === "active"
                                          ? "success"
                                          : "secondary"
                                      }
                                    >
                                      {risk.status}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() =>
                                        handleViewManagedRisk(risk)
                                      }
                                    >
                                      <i className="fas fa-eye"></i>
                                    </Button>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        No managed risks found.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <small className="text-muted me-auto">
            <i className="fas fa-eye me-1"></i>Viewing as Admin
          </small>
          <Button
            variant="secondary"
            onClick={() => setShowDashboardModal(false)}
          >
            <i className="fas fa-times me-2"></i>Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ MODAL: MANAGED RISK DETAIL ============ */}
      <Modal
        show={showRiskDetailModal}
        onHide={() => setShowRiskDetailModal(false)}
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: brandColors.secondaryBlue, color: "white" }}
        >
          <Modal.Title>
            <i className="fas fa-shield-alt me-2"></i>Managed Risk Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRisk && (
            <>
              <Card className="mb-3 border-0 shadow-sm">
                <Card.Header
                  style={{
                    backgroundColor: brandColors.primaryGreen,
                    color: "white",
                  }}
                >
                  <h6 className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>Risk Overview
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>
                          <i className="fas fa-hashtag me-2"></i>Risk ID:
                        </strong>{" "}
                        <Badge bg="secondary">{selectedRisk.riskId}</Badge>
                      </p>
                      <p className="mb-2">
                        <strong>
                          <i className="fas fa-tag me-2"></i>Category:
                        </strong>{" "}
                        <Badge
                          style={{ backgroundColor: brandColors.secondaryBlue }}
                          className="text-white"
                        >
                          {selectedRisk.category}
                        </Badge>
                      </p>
                      <p className="mb-2">
                        <strong>
                          <i className="fas fa-signal me-2"></i>Severity:
                        </strong>{" "}
                        <Badge
                          bg={
                            selectedRisk.severity === "Critical"
                              ? "danger"
                              : selectedRisk.severity === "High"
                                ? "warning"
                                : "success"
                          }
                        >
                          {selectedRisk.severity}
                        </Badge>
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>
                          <i className="fas fa-shield-alt me-2"></i>Asset:
                        </strong>{" "}
                        {selectedRisk.assetAffected || "N/A"}
                      </p>
                      <p className="mb-2">
                        <strong>
                          <i className="fas fa-user-shield me-2"></i>Owner:
                        </strong>{" "}
                        {selectedRisk.riskOwner || "N/A"}
                      </p>
                      <p className="mb-2">
                        <strong>
                          <i className="fas fa-flag me-2"></i>Status:
                        </strong>{" "}
                        <Badge
                          bg={
                            selectedRisk.status === "active"
                              ? "success"
                              : "secondary"
                          }
                        >
                          {selectedRisk.status}
                        </Badge>
                      </p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">
                    <i className="fas fa-align-left me-2"></i>Risk Description
                  </h6>
                </Card.Header>
                <Card.Body>
                  <p style={{ lineHeight: 1.6 }}>{selectedRisk.risk}</p>
                </Card.Body>
              </Card>
              {selectedRisk.recommendation && (
                <Card className="mt-3 border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-lightbulb me-2"></i>Recommendation
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p>{selectedRisk.recommendation}</p>
                  </Card.Body>
                </Card>
              )}
              {selectedRisk.evidenceFileName && (
                <Card className="mt-3 border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-paperclip me-2"></i>Evidence
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-2">
                      <strong>File Name:</strong>{" "}
                      {selectedRisk.evidenceFileName}
                    </p>
                    {selectedRisk.evidenceUrl && (
                      <Button
                        size="sm"
                        style={{ backgroundColor: brandColors.secondaryBlue }}
                        href={selectedRisk.evidenceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-download me-2"></i>Download
                        Evidence
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button
            variant="secondary"
            onClick={() => setShowRiskDetailModal(false)}
          >
            <i className="fas fa-times me-2"></i>Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ MODAL: SHARE DASHBOARD ============ */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Share Dashboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Recipient Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleShareDashboard}>
            Share
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ MODAL: SHARED WITH ME ============ */}
      <Modal
        show={showSharedWithMeModal}
        onHide={() => setShowSharedWithMeModal(false)}
        size="xl" 
      >
        <Modal.Header closeButton>
          <Modal.Title>
           Dashboards Shared With Me
          </Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              fetchSharedWithMe();
              triggerToast("Refreshing...");
            }}
            disabled={loadingSharedWithMe}
            className="ms-2"
          >
            <i
              className={`fas fa-sync-alt ${loadingSharedWithMe ? "fa-spin" : ""}`}
            ></i>
          </Button>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <p className="text-muted mb-4">
            View and import complete dashboards that external organisations have
            shared with you.
          </p>
          {loadingSharedWithMe ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="ImportRiskTopMargin">Loading...</p>
            </div>
          ) : sharedWithMeDashboards.length > 0 ? (
            <div>
              {sharedWithMeDashboards.map((dashboard, idx) => {
                const isImporting =
                  importingSharedDashboard === dashboard.shareId;
                const isImported = importedSharedDashboards.has(
                  dashboard.shareId,
                );
                return (
                  <Card key={idx} className="mb-3 border shadow-sm">
                    <Card.Body className="p-4">
                      <Row>
                        <Col md={8}>
                          <div className="d-flex align-items-center mb-3">
                            <div
                              className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{
                                width: "50px",
                                height: "50px",
                                fontSize: "20px",
                              }}
                            >
                              <i className="fas fa-building"></i>
                            </div>
                            <div>
                              <h6 className="mb-0">{dashboard.ownerName}</h6>
                              <small className="text-muted">
                                {dashboard.ownerEmail}
                              </small>
                              {isImported && (
                                <Badge bg="success" className="ms-2">
                                  <i className="fas fa-check me-1"></i>Imported
                                </Badge>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col
                          md={4}
                          className="d-flex flex-column align-items-center justify-content-center gap-2"
                        >
                          {!isImported ? (
                            <Button
                              variant="primary"
                              size="lg"
                              onClick={() =>
                                handleImportSharedDashboard(
                                  dashboard.shareId,
                                  dashboard.ownerName,
                                  dashboard.ownerEmail,
                                )
                              }
                              disabled={isImporting}
                              className="w-100"
                            >
                              {isImporting ? (
                                <>
                                  <Spinner
                                    size="sm"
                                    animation="border"
                                    className="me-2"
                                  />
                                  Importing...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-download me-2"></i>Import
                                  Dashboard
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="danger"
                              size="lg"
                              onClick={() =>
                                handleRemoveSharedDashboard(
                                  dashboard.shareId,
                                  dashboard.ownerName,
                                  dashboard.ownerEmail,
                                )
                              }
                              className="w-100"
                            >
                              <i className="fas fa-trash-alt me-2"></i>Remove
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Alert variant="info" className="text-center py-5">
              
              <h5>No Shared Dashboards</h5>
              <p className="mb-0">
                When external organisations share dashboards with you, they will
                appear here.
              </p>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <small className="text-muted me-auto">
            <i className="fas fa-info-circle me-1"></i>Imported data merges into
            consolidated view
          </small>
          <Button
            variant="secondary"
            onClick={() => setShowSharedWithMeModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ MODAL: INSIGHT DETAIL ============ */}
      <Modal
        show={showInsightDetailModal}
        onHide={() => setShowInsightDetailModal(false)}
        size="xl"
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#1b2a4a", color: "#fff" }}
        >
          <Modal.Title>
            <i className="fas fa-shield-alt me-2"></i>Risk Insight Detail —{" "}
            {selectedInsightForDetail?.riskId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {selectedInsightForDetail && (
            <>
              <Row className="mb-4">
                <Col md={8}>
                  <h6 className="text-muted text-uppercase mb-2">
                    Risk Description
                  </h6>
                  <p style={{ lineHeight: 1.6 }}>
                    {selectedInsightForDetail.risk}
                  </p>
                </Col>
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <div className="mb-3">
                        <strong>Severity:</strong>
                        <Badge
                          bg={
                            selectedInsightForDetail.severity === "Critical"
                              ? "danger"
                              : selectedInsightForDetail.severity === "High"
                                ? "warning"
                                : selectedInsightForDetail.severity === "Medium"
                                  ? "primary"
                                  : "success"
                          }
                          className="ms-2"
                        >
                          {selectedInsightForDetail.severity}
                        </Badge>
                      </div>
                      <div className="mb-3">
                        <strong>Category:</strong>{" "}
                        <Badge bg="info" className="ms-2">
                          {selectedInsightForDetail.category}
                        </Badge>
                      </div>
                      <div className="mb-3">
                        <strong>Source:</strong>{" "}
                        {getSourceBadge(selectedInsightForDetail)}
                      </div>
                      {selectedInsightForDetail.sourceEmail && (
                        <div className="mb-3">
                          <strong>From:</strong>{" "}
                          <small className="ms-2">
                            {selectedInsightForDetail.sourceEmail}
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {selectedInsightForDetail.linkedControls &&
                selectedInsightForDetail.linkedControls.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-muted text-uppercase mb-2">
                      <i className="fas fa-link me-2"></i>Linked Controls
                    </h6>
                    <Table bordered hover size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Control ID</th>
                          <th>Control Name</th>
                          <th>Maturity</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInsightForDetail.linkedControls.map(
                          (ctrl, idx) => (
                            <tr key={idx}>
                              <td>
                                <Badge bg="secondary">{ctrl.controlId}</Badge>
                              </td>
                              <td>{ctrl.controlName}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="progress flex-grow-1"
                                    style={{ height: "8px" }}
                                  >
                                    <div
                                      className="progress-bar bg-info"
                                      style={{ width: ctrl.maturity }}
                                    ></div>
                                  </div>
                                  <small className="fw-bold">
                                    {ctrl.maturity}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <Badge
                                  bg={
                                    ctrl.status === "implemented"
                                      ? "success"
                                      : "warning"
                                  }
                                >
                                  {ctrl.status}
                                </Badge>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

              <div className="mb-4">
                <h6 className="text-muted text-uppercase mb-2">
                  <i className="fas fa-lightbulb me-2"></i>Recommendation
                </h6>
                <Alert variant="info" className="mb-0">
                  <small>{selectedInsightForDetail.recommendation}</small>
                </Alert>
              </div>

              <div
                className="d-flex gap-3 flex-wrap text-muted"
                style={{ fontSize: "0.8rem" }}
              >
                <span>
                  <strong>Asset:</strong>{" "}
                  {selectedInsightForDetail.assetAffected}
                </span>
                <span>
                  <strong>Threat:</strong>{" "}
                  {selectedInsightForDetail.threatSource}
                </span>
                <span>
                  <strong>Source:</strong>{" "}
                  {selectedInsightForDetail.source || "AI Trust Controls"}
                </span>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInsightDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ TOAST ============ */}
      <ToastContainer position="top-end" className="custom-toast-container">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg="success"
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default GlobalRiskOverview;
